import React, { useEffect, useMemo, useState } from "react";

import { useSearchDatasets } from "api/api-hooks/datasets-hooks"; // Import your custom hook
import { DatasetQueryParams } from "api/model/dataset/DatasetDataInfoResponse";
import ChartInDialogButton from "components/basic/chart-in-dialog-button/ChartInDialogButton";
import CopyTextWithButton from "components/basic/copy-text-with-button/CopyTextWithButton";
import {
  AutoComplete,
  AutoCompleteChangeEvent,
  AutoCompleteCompleteEvent,
} from "primereact/autocomplete";
import { T } from "services/lang/LanguageService";
import { DropdownItem } from "utils/general-models";
import { isDatasetIdentifier } from "utils/utils";
import "./style.scss";

interface IDatasetMultiSelectProps {
  value?: string[] | null;
  onChange: (e: string[]) => void;
  style?: React.CSSProperties;
  disabled?: boolean;
  proxied?: boolean
  deleted?: boolean
}

function DatasetMultiSelect(props: IDatasetMultiSelectProps) {
  const [selectedDatasets, setSelectedDatasets] = useState<DropdownItem<string>[] | null>(null);
  const [filteredDatasets, setFilteredDatasets] = useState<DropdownItem<string>[]>([]);
  const [searchQueryParams, setSearchQueryParams] = useState<DatasetQueryParams>({
    query: "",
    limit: 100,
    identifiers: [],
    deleted: props.deleted,
    proxied: props.proxied
  });

  const validIdentifiers = useMemo(() => props.value?.filter(id => id !== "") || [], [props.value]);

  const { data } = useSearchDatasets({ query: null, limit: 100, identifiers: validIdentifiers, proxied: true }, {
    enabled: validIdentifiers.length > 0
  });

  const { data: searchResults } = useSearchDatasets(searchQueryParams, {
    enabled: Number(searchQueryParams.query?.length) >= 3 || !!searchQueryParams.identifiers?.length,
  });

  useEffect(() => {
    if (validIdentifiers.length > 0 && data && props.value) {
      const apiSelections = data.map(
        it =>
          ({
            value: it.identifier,
            label: it.toString(),
          }) as DropdownItem<string>
      );

      const emptySelections = props.value
        .filter(it => it === "")
        .map(
          () =>
            ({
              value: "",
              label: "Empty",
            }) as DropdownItem<string>
        );

      setSelectedDatasets([...apiSelections, ...emptySelections]);
    }
  }, [data, validIdentifiers, props.value]);

  useEffect(() => {
    if (searchResults) {
      const selectedDatasetIdentifiers = selectedDatasets?.map(dataset => dataset.value) || [];
      const selections = searchResults
        .filter(it => !selectedDatasetIdentifiers.includes(it.identifier as string))
        .map(it => ({ value: it.identifier, label: `${it.toString()} | ${it.datasetClass}` }) as DropdownItem<string>);
      setFilteredDatasets(selections);
    }
  }, [searchResults, selectedDatasets]);

  const searchDatasets = (e: AutoCompleteCompleteEvent) => {
    const query = e.query.trim();
    if (isDatasetIdentifier(query)) {
      setSearchQueryParams(prev => ({ ...prev, query: "", limit: 20, identifiers: [query] }));
      return;
    }
    setSearchQueryParams(prev => ({ ...prev, query, limit: 100, identifiers: [] }));
  };

  const onChange = (e: AutoCompleteChangeEvent) => {
    setSelectedDatasets(e.value);

    if (typeof e.value === "string") return; // Ignore string auto complete dumb input

    props.onChange(e.value?.map((item: DropdownItem<string>) => item.value) || []);
    setSearchQueryParams(prev => ({ ...prev, query: "", limit: 100, identifiers: [] }));
  };

  const chipTemplate = (item: DropdownItem<string>) => {
    return item.value.length > 0 ? (
      <div className="flex nowrap gap-1 align-items-baseline mr-2">
        <span className="w-16rem sm:w-full overflow-x-auto">{item.label}</span>
        <CopyTextWithButton text="" copyableText={item.value} />
        <ChartInDialogButton identifier={item.value} header={T("word.dataset")} />
      </div>
    ) : (
      <div className="flex nowrap gap-1 align-items-baseline mr-2">
        <span>{item.label}</span>
      </div>
    );
  };

  return (
    <AutoComplete
      placeholder={T("message.type-for-search-datasets")}
      multiple
      value={selectedDatasets}
      suggestions={filteredDatasets}
      onChange={onChange}
      completeMethod={searchDatasets}
      minLength={3}
      className="dataset-multi-select"
      inputClassName="block"
      field="label"
      style={{ width: "100%", ...props.style }}
      disabled={props.disabled}
      selectedItemTemplate={chipTemplate}
    />
  );
}

export default DatasetMultiSelect;
